import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Gallery,
  GalleryFolderResponse,
  PhotoGalleryStats,
  GalleryDetail,
  UpdateGalleryStatus,
  GalleryStats,
  GalleryHighlight
} from '@/lib/api/types'

export const galleryService = {
  // Create a new gallery with photo URLs
  createGallery: async (data: {
    folderId: string;
    title: string;
    photoUrl: string[];
    caption?: string;
    altText?: string;
    description?: string;
    isKeyHighlight: boolean;
    isMultimedia: boolean;
    status: 'DRAFT' | 'PUBLISH';
  }): Promise<ApiResponse<Gallery>> => {
    return await post<ApiResponse<Gallery>>(
      'photo-galleries/admin/private',
      data
    )
  },

  // Update an existing gallery
  updateGallery: async (
    galleryId: string,
    data: {
      folderId?: string;
      title?: string;
      photoUrl?: string;
      isKeyHighlight?: boolean;
      isMultimedia?: boolean;
      status?: 'DRAFT' | 'PUBLISH';
    }
  ): Promise<ApiResponse<Gallery>> => {
    return await put<ApiResponse<Gallery>>(
      `photo-galleries/admin/private/${galleryId}`,
      data
    )
  },

  // Delete a gallery
  deleteGallery: async (galleryId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `photo-galleries/admin/private/${galleryId}`
    )
  },

  // Delete a photo from gallery
  deletePhoto: async (photoId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `photo-galleries/admin/private/removephoto/${photoId}`
    )
  },

  // Update gallery status
  // PUT api/v1/galleries/admin/private/{galleryId}/status
  // Body: { "status": "PUBLISH" | "DRAFT" }
  updateGalleryStatus: async (
    galleryId: string,
    data: UpdateGalleryStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `photo-galleries/admin/private/${galleryId}/status`,
      data
    )
  },

  // Add photos to gallery
  addPhotoToGallery: async (
    galleryId: string,
    photoUrls: string[]
  ): Promise<ApiResponse<null>> => {
    return await post<ApiResponse<null>>(
      `photo-galleries/admin/private/addphoto/${galleryId}`,
      { photoUrls }
    )
  },

  // Filter galleries with pagination (admin)
  filterGalleries: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: Record<string, any> = {}
  ): Promise<ApiResponse<PaginatedResponse<GalleryFolderResponse>>> => {
    return await post(
      `photo-galleries/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },

  // Get galleries (public endpoint with pagination)
  getGalleries: async (
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<GalleryFolderResponse[]>> => {
    return await get<ApiResponse<GalleryFolderResponse[]>>(
      `photo-galleries/private?size=${params.size}&page=${params.page}`
    )
  },

  // Get gallery details by ID
  getGalleryById: async (galleryId: string): Promise<ApiResponse<GalleryDetail>> => {
    return await get<ApiResponse<GalleryDetail>>(
      `photo-galleries/private/gallery/${galleryId}`
    )
  },

  // Get folder details (galleries grouped by folder)
  getFolderDetails: async (
    folderId: string
  ): Promise<ApiResponse<GalleryFolderResponse>> => {
    return await get<ApiResponse<GalleryFolderResponse>>(
      `photo-galleries/private/folder/${folderId}`
    )
  },

  // Get total gallery stats
  getTotalStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'photo-galleries/private/stats/total'
    )
  },

  // Get published gallery stats
  getPublishedStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'photo-galleries/private/stats/published'
    )
  },

  // Get draft gallery stats
  getDraftStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'photo-galleries/private/stats/draft'
    )
  },

  // Get all gallery stats (combined)
  getGalleryStats: async (): Promise<ApiResponse<GalleryStats>> => {
    const [totalResponse, publishedResponse, draftResponse] = await Promise.all([
      galleryService.getTotalStats(),
      galleryService.getPublishedStats(),
      galleryService.getDraftStats()
    ])

    const stats: GalleryStats = {
      total: totalResponse.data || 0,
      published: publishedResponse.data || 0,
      draft: draftResponse.data || 0
    }

    return {
      success: true,
      message: 'Gallery stats retrieved successfully',
      data: stats
    }
  },

  // Search galleries
  searchGalleries: async (
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<GalleryFolderResponse>>> => {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      size: params.size.toString()
    })

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<PaginatedResponse<GalleryFolderResponse>>>(
      `photo-galleries/private/search?${queryParams.toString()}`
    )
  },

  // Get Home Page Highlights
  getHomePageHighlights: async (type: string = 'HOME_PAGE_IS_KEY_HIGHLIGHT'): Promise<ApiResponse<GalleryHighlight[]>> => {
    return await get<ApiResponse<GalleryHighlight[]>>(
      `photo-galleries/public/highlights?type=${type}`
    )
  },

  // Update Home Page Highlights
  updateHomePageHighlights: async (photoGalleryIds: string[], type: string = 'HOME_PAGE_IS_KEY_HIGHLIGHT'): Promise<ApiResponse<GalleryHighlight[]>> => {
    return await post<ApiResponse<GalleryHighlight[]>>(
      'photo-galleries/private/highlight',
      {
        photoGalleryIds,
        highlightType: type
      }
    )
  },

    // Get photo gallery dashboard stats
  getPhotoGalleryStats: async (): Promise<ApiResponse<PhotoGalleryStats>> => {
    return await get<ApiResponse<PhotoGalleryStats>>(
      'photo-galleries/private/stats'
    )
  }
}
